import type { AuthState } from 'lib/authState';

/**
 * Features whose nav entries are wired up but hidden until they ship. Flip a
 * flag to true once the pages behind it exist.
 *   news    -- blog/announcements, served from /news and /news/:slug
 *   myTeams -- teams a logged-in user follows, managed from their profile
 */
export const FEATURES = {
    news: true,
    myTeams: true
};

export type NavPlacement = 'primary' | 'account' | 'footer';

export interface NavContext {
    auth: AuthState;
    features: typeof FEATURES;
}

export interface NavItem {
    title: string;
    path: string;
    /**
     * primary -- top-bar links for browsing (and the top of the mobile sheet)
     * account -- the account menu, only rendered for logged-in users
     * footer  -- secondary links; nothing should live *only* here
     */
    placement: NavPlacement;
    visible?: (ctx: NavContext) => boolean;
}

// Single source of truth for every nav link in the app. Nav, AccountMenu,
// MobileNav and Footer all render from this list, so a link can't end up in
// one menu and missing from another.
export const NAV_ITEMS: NavItem[] = [
    { title: 'Matchups', path: '/matchup', placement: 'primary' },
    {
        title: 'News',
        path: '/news',
        placement: 'primary',
        visible: ({ features }) => features.news
    },

    {
        title: 'Manage teams',
        path: '/profile#teams',
        placement: 'account',
        visible: ({ features }) => features.myTeams
    },
    { title: 'Profile', path: '/profile', placement: 'account' },
    {
        title: 'Developer',
        path: '/developer',
        placement: 'account',
        visible: ({ auth }) => auth.canAccessDeveloper
    },
    {
        title: 'Admin',
        path: '/admin/stats',
        placement: 'account',
        visible: ({ auth }) => auth.isAdmin
    },

    { title: 'About', path: '/about', placement: 'footer' },
    { title: 'Support', path: '/support', placement: 'footer' },
    { title: 'Report an issue', path: '/bug-report', placement: 'footer' }
];

export function navItemsFor(placement: NavPlacement, ctx: NavContext): NavItem[] {
    return NAV_ITEMS.filter(
        (item) => item.placement === placement && (!item.visible || item.visible(ctx))
    );
}
